import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import "./styles/pdf_viewer.css";
import "./styles/pdf_text_layer.css";
import { Spinner } from "@air/primitive-spinner";
import { useBreakpointsContext } from "@air/provider-media-query";
import classNames from "classnames";
import noop from "lodash/noop";
import { getDocument, GlobalWorkerOptions, PixelsPerInch, version } from "pdfjs-dist";
import { memo, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, useTransition } from "react";
import { PDF_LIST_SCROLL_MARGIN, PDFPagesVirtualizedList } from "./components/PDFPagesVirtualizedList";
import { PDF_OUTLINE_WIDTH, PDFViewerOutline } from "./components/PDFViewerOutline/PDFViewerOutline";
import { PDFViewerPage } from "./components/PDFViewerPage";
import { ScrollToCurrentPDFResultObserver } from "./components/ScrollToCurrentPDFResultObserver";
import { PDFCurrentSearchResultElementContextProvider } from "./contexts/PDFCurrentSearchResultElementContext";
import { PDFViewerControlProvider } from "./contexts/PDFViewerControlContext";
import { PDFViewerSearchContextProvider } from "./contexts/PDFViewerSearch/PDFViewerSearchContext";
import { useGetPDFPageHeight } from "./hooks/useGetPDFPageHeight";
import { useMouseWheelZoom } from "./hooks/useMouseWheelZoom";
import { usePDFTextSelectionListener } from "./hooks/usePDFTextSelectionListener";
import { usePersistScrollPositionOnZoom } from "./hooks/usePersistScrollPositionOnZoom";
import { pdfScaleSteps } from "./utils/constants";
import { findIndexOfLowestClosestNumber } from "./utils/findIndexOfLowestClosestNumber";
import { findScaleForDimensions } from "./utils/findScaleForDimensions";
import { PDFLinkService } from "./utils/PdfLinkService";
import { PDFPagesCache } from "./utils/PDFPagesCache";
GlobalWorkerOptions.workerSrc = "/pdf-js/".concat(version, "/pdf.worker.min.mjs");
const cMapUrl = "https://unpkg.com/pdfjs-dist@".concat(version, "/cmaps/");
const standardFontsUrl = "https://unpkg.com/pdfjs-dist@".concat(version, "/standard_fonts/");
const DEFAULT_SCALE = 1;
var _pdfScaleSteps_findIndex;
const DEFAULT_SCALE_INDEX = (_pdfScaleSteps_findIndex = pdfScaleSteps.findIndex((step)=>step === 0.75)) !== null && _pdfScaleSteps_findIndex !== void 0 ? _pdfScaleSteps_findIndex : 0;
export const PDFViewer = /*#__PURE__*/ memo((param)=>{
    let { pdfData, children, renderOutlinePageMenu, renderPageLayers, handleRef, onPDFLoaded, errorView, onError, hideInternalOutline, assetModalImprovements = false } = param;
    const pdfPagesCacheRef = useRef();
    const listRef = useRef(null);
    const containerRef = useRef(null);
    const linkServiceRef = useRef();
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    usePDFTextSelectionListener();
    const onPDFLoadedRef = useRef(onPDFLoaded);
    onPDFLoadedRef.current = onPDFLoaded;
    // Inline scale management
    const [scaleStep, setScaleStep] = useState(DEFAULT_SCALE_INDEX);
    const currentScale = pdfScaleSteps[scaleStep];
    const [_, startTransition] = useTransition();
    const updateScale = useCallback((newScale)=>{
        startTransition(()=>{
            const newScaleIndex = findIndexOfLowestClosestNumber(pdfScaleSteps, newScale);
            setScaleStep(newScaleIndex);
        });
    }, []);
    const zoomIn = useCallback(()=>{
        startTransition(()=>setScaleStep((prev)=>prev < pdfScaleSteps.length - 1 ? prev + 1 : prev));
    }, []);
    const zoomOut = useCallback(()=>{
        startTransition(()=>setScaleStep((prev)=>prev > 0 ? prev - 1 : prev));
    }, []);
    const setScaleForDimensions = useCallback((scaleToSet)=>{
        const scaleIndex = findIndexOfLowestClosestNumber(pdfScaleSteps, scaleToSet);
        setScaleStep(scaleIndex);
    }, []);
    const [pdfDocument, setPDFDocument] = useState();
    const [currentPage, setCurrentPage] = useState(1);
    const [isOutlineOpen, setIsOutlineOpen] = useState(true);
    const { isAboveLargeScreen } = useBreakpointsContext();
    const pdfLoadingTaskRef = useRef();
    useMouseWheelZoom({
        zoomIn,
        zoomOut
    });
    usePersistScrollPositionOnZoom({
        listRef,
        currentScale,
        currentPage
    });
    useImperativeHandle(handleRef, ()=>({
            scrollToPage: (pageNumber)=>{
                var _listRef_current;
                (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.scrollToIndex(pageNumber - 1);
            }
        }));
    useEffect(()=>{
        setPDFDocument(null);
    }, [
        pdfData
    ]);
    useEffect(()=>{
        if (pdfDocument && listRef.current) {
            requestAnimationFrame(()=>{
                var _onPDFLoadedRef_current;
                return (_onPDFLoadedRef_current = onPDFLoadedRef.current) === null || _onPDFLoadedRef_current === void 0 ? void 0 : _onPDFLoadedRef_current.call(onPDFLoadedRef, pdfDocument);
            });
        }
    }, [
        pdfDocument
    ]);
    useLayoutEffect(()=>{
        if (pdfDocument) {
            linkServiceRef.current = new PDFLinkService({
                pdfViewer: {
                    scrollToPage: (param)=>{
                        // Handle both direct number and object parameter formats
                        const pageNumber = typeof param === "number" ? param : param === null || param === void 0 ? void 0 : param.pageNumber;
                        if (pageNumber) {
                            var _listRef_current;
                            (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.scrollToIndex(pageNumber - 1);
                        }
                    }
                },
                pdfDocument
            });
        }
    }, [
        pdfDocument
    ]);
    const isOutliveVisibleRef = useRef(isOutlineOpen);
    isOutliveVisibleRef.current = isOutlineOpen;
    const onAllPagesLoadInCache = useCallback(()=>{
        var _listRef_current;
        (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.updateItemsSize();
    }, []);
    const onAllPagesLoadInCacheRef = useRef(onAllPagesLoadInCache);
    onAllPagesLoadInCacheRef.current = onAllPagesLoadInCache;
    useLayoutEffect(()=>{
        const loadPDF = async (pdfData)=>{
            if (pdfLoadingTaskRef.current) {
                await pdfLoadingTaskRef.current.destroy();
                pdfLoadingTaskRef.current = null;
            }
            pdfLoadingTaskRef.current = getDocument({
                isOffscreenCanvasSupported: true,
                cMapUrl,
                // pdf.js transfers "data" to worker and loses control on it.
                // we want to pass copy of "data" to getDocument to avoid crashes when PDFViewer rerenders
                data: pdfData.slice(0),
                standardFontDataUrl: standardFontsUrl
            });
            pdfLoadingTaskRef.current.promise.then(async (pdfDocument)=>{
                pdfPagesCacheRef.current = new PDFPagesCache(pdfDocument, onAllPagesLoadInCacheRef.current);
                if (!containerRef.current) return;
                const firstPage = await pdfDocument.getPage(1);
                const firstPageViewport = firstPage.getViewport({
                    scale: PixelsPerInch.PDF_TO_CSS_UNITS
                });
                const availableWidth = containerRef.current.clientWidth - (isOutliveVisibleRef.current ? PDF_OUTLINE_WIDTH : 0);
                const availableHeight = containerRef.current.clientHeight;
                const scaleToSet = findScaleForDimensions({
                    defaultScale: DEFAULT_SCALE,
                    elementSize: firstPageViewport,
                    containerSize: {
                        width: availableWidth,
                        height: availableHeight - PDF_LIST_SCROLL_MARGIN * 2
                    }
                });
                setScaleForDimensions(scaleToSet);
                setPDFDocument(pdfDocument);
            }, (error)=>{
                var _onErrorRef_current;
                (_onErrorRef_current = onErrorRef.current) === null || _onErrorRef_current === void 0 ? void 0 : _onErrorRef_current.call(onErrorRef, {
                    error,
                    context: "Failed to load pdf"
                });
            });
        };
        if (pdfData) {
            loadPDF(pdfData);
        }
    }, [
        pdfData,
        setScaleForDimensions
    ]);
    const { getPageHeight } = useGetPDFPageHeight({
        pdfDocument,
        currentScale,
        pdfPagesCacheRef
    });
    const pageRowRenderer = useCallback((param)=>{
        let { index } = param;
        if (!linkServiceRef || !pdfPagesCacheRef) {
            return null;
        }
        return /*#__PURE__*/ _jsx(PDFViewerPage, {
            cachedHeight: getPageHeight(index),
            linkServiceRef: linkServiceRef,
            pdfPageCacheRef: pdfPagesCacheRef,
            scale: pdfScaleSteps[scaleStep],
            pageNumber: index + 1,
            renderPageLayers: renderPageLayers
        });
    }, [
        getPageHeight,
        renderPageLayers,
        scaleStep
    ]);
    const onTopVisibleElementChange = useCallback((index)=>{
        setCurrentPage(index + 1);
    }, [
        setCurrentPage
    ]);
    const changePage = useCallback((pageNumber)=>{
        var _listRef_current;
        (_listRef_current = listRef.current) === null || _listRef_current === void 0 ? void 0 : _listRef_current.scrollToIndex(pageNumber - 1);
    }, [
        listRef
    ]);
    // Create PDF controls object for render props
    const pdfControls = useMemo(()=>{
        var _pdfDocument_numPages;
        return {
            scale: currentScale,
            updateScale,
            isOutlineOpen,
            setIsOutlineOpen,
            currentPage,
            numPages: (_pdfDocument_numPages = pdfDocument === null || pdfDocument === void 0 ? void 0 : pdfDocument.numPages) !== null && _pdfDocument_numPages !== void 0 ? _pdfDocument_numPages : 0,
            changePage,
            pdfDocument: pdfDocument !== null && pdfDocument !== void 0 ? pdfDocument : null,
            pdfPagesCacheRef: pdfPagesCacheRef.current ? pdfPagesCacheRef : null,
            renderOutlinePageMenu
        };
    }, [
        currentScale,
        updateScale,
        isOutlineOpen,
        setIsOutlineOpen,
        currentPage,
        pdfDocument,
        changePage,
        renderOutlinePageMenu
    ]);
    if (!pdfDocument) {
        return /*#__PURE__*/ _jsxs("div", {
            ref: containerRef,
            className: "flex size-full items-center justify-center",
            children: [
                /*#__PURE__*/ _jsx(PDFViewerControlProvider, {
                    scale: currentScale,
                    updateScale: updateScale,
                    numPages: 0,
                    currentPage: 1,
                    changePage: noop,
                    isOutlineOpen: false,
                    setIsOutlineOpen: setIsOutlineOpen,
                    children: /*#__PURE__*/ _jsx(PDFViewerSearchContextProvider, {
                        pdfDocument: null,
                        pdfPagesCache: null,
                        children: children(pdfControls)
                    })
                }),
                !!errorView ? errorView : /*#__PURE__*/ _jsx(Spinner, {
                    className: "size-8 text-teal-9"
                })
            ]
        });
    }
    const isOutlineVisible = !hideInternalOutline && isOutlineOpen && isAboveLargeScreen;
    var _pdfPagesCacheRef_current;
    return /*#__PURE__*/ _jsxs("div", {
        ref: containerRef,
        className: classNames("pdfViewer relative flex size-full flex-col", assetModalImprovements ? "bg-grey-1" : "bg-black"),
        children: [
            !hideInternalOutline && /*#__PURE__*/ _jsx(PDFViewerOutline, {
                pagesCount: pdfDocument.numPages,
                pdfPageCacheRef: pdfPagesCacheRef,
                isOpen: isOutlineVisible,
                currentPage: currentPage,
                onPageClick: changePage,
                renderOutlinePageMenu: renderOutlinePageMenu
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "relative flex h-full",
                style: {
                    width: isOutlineVisible ? "calc(100% - ".concat(PDF_OUTLINE_WIDTH, "px)") : "100%",
                    marginLeft: isOutlineVisible ? PDF_OUTLINE_WIDTH : 0
                },
                children: /*#__PURE__*/ _jsx(PDFViewerControlProvider, {
                    scale: currentScale,
                    updateScale: updateScale,
                    numPages: pdfDocument.numPages,
                    currentPage: currentPage,
                    changePage: changePage,
                    isOutlineOpen: isOutlineVisible,
                    setIsOutlineOpen: setIsOutlineOpen,
                    children: /*#__PURE__*/ _jsx(PDFViewerSearchContextProvider, {
                        pdfDocument: pdfDocument,
                        pdfPagesCache: (_pdfPagesCacheRef_current = pdfPagesCacheRef.current) !== null && _pdfPagesCacheRef_current !== void 0 ? _pdfPagesCacheRef_current : null,
                        children: /*#__PURE__*/ _jsxs(PDFCurrentSearchResultElementContextProvider, {
                            children: [
                                /*#__PURE__*/ _jsx(PDFPagesVirtualizedList, {
                                    onTopVisibleElementChange: onTopVisibleElementChange,
                                    ref: listRef,
                                    getPageHeight: getPageHeight,
                                    rowsCount: pdfDocument.numPages,
                                    children: pageRowRenderer
                                }),
                                children(pdfControls),
                                /*#__PURE__*/ _jsx(ScrollToCurrentPDFResultObserver, {
                                    listRef: listRef,
                                    currentPageNumber: currentPage
                                })
                            ]
                        })
                    })
                })
            })
        ]
    });
});
PDFViewer.displayName = "PDFViewer";
